import { NgClass } from "@angular/common";
import {
  Component,
  computed,
  DestroyRef,
  signal,
  Signal,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router, RouterModule } from "@angular/router";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { DividerModule } from "primeng/divider";
import { DialogService } from "primeng/dynamicdialog";
import { MenuModule } from "primeng/menu";
import { filter, switchMap } from "rxjs";
import { TeamMemberRoleEnum } from "../../enum/team-member-role.enum";
import {
  NavChildItemInterface,
  NavItemInterface,
} from "../../interfaces/nav.interface";
import { Project } from "../../interfaces/project.interface";
import { ProjectManagementService } from "../../services/project-management.service";
import { UserService } from "../../services/user.service";
import { AddNewProjectDialogComponent } from "../add-new-project-dialog/add-new-project-dialog.component";

@Component({
  selector: "app-nav",
  imports: [
    TranslocoDirective,
    RouterModule,
    ButtonModule,
    MenuModule,
    DividerModule,
    NgClass,
  ],
  templateUrl: "./nav.component.html",
  styleUrl: "./nav.component.scss",
})
export class NavComponent {
  private allMenuItems: WritableSignal<NavItemInterface[]> = signal([]);
  menuItems: Signal<NavItemInterface[]>;
  bottomMenuItems: Signal<NavItemInterface[]>;
  expanded: WritableSignal<boolean> = signal(true);

  constructor(
    private router: Router,
    private t: TranslocoService,
    private userService: UserService,
    private projectService: ProjectManagementService,
    private dialogService: DialogService,
    private destroyRef: DestroyRef,
    private messageService: MessageService,
  ) {
    this.setMenu();
    this.menuItems = computed(() => this.filterByRoles(this.allMenuItems()));
    this.bottomMenuItems = signal([
      // TODO Settings page doesn't exist yet.
      // {
      //   label: this.t.translate("nav.settings"),
      //   routerLink: "/settings",
      //   icon: "pi pi-cog",
      // },
      {
        label: this.t.translate("nav.logout"),
        command: () => {
          this.userService.logout().subscribe();
        },
        icon: "pi pi-sign-out",
      },
    ]);
  }

  toggleMenu(): void {
    this.expanded.set(!this.expanded());
  }

  navItemClicked(clickedItem: NavItemInterface | NavChildItemInterface) {
    if (clickedItem.routerLink) {
      this.router.navigate([clickedItem.routerLink]);
    }

    if (clickedItem.command) {
      clickedItem.command();
    }
  }

  setMenu(): void {
    this.allMenuItems.set([
      // {
      //   label: this.t.translate("nav.dashboard"),
      //   icon: "pi pi-gauge",
      //   routerLink: "/overview",
      //   items: [
      //     {
      //       label: this.t.translate("nav.overview"),
      //       routerLink: "/overview",
      //     },
      //   ],
      // },
      {
        label: this.t.translate("nav.projectManagement"),
        icon: "pi pi-building",
        routerLink: "/project-management/overview",
        roles: [
          TeamMemberRoleEnum.SUPER_ADMIN,
          TeamMemberRoleEnum.ADMIN,
          TeamMemberRoleEnum.PROJECT_MANAGER,
        ],
        items: [
          {
            label: this.t.translate("nav.addNewProject"),
            icon: "pi pi-plus",
            command: () => {
              this.router.navigate(["/project-management/projects"]);
              this.openAddNewProjectDialog();
            },
          },
          {
            label: this.t.translate("nav.overview"),
            icon: "pi pi-chart-line",
            routerLink: "/project-management/overview",
          },
          {
            label: this.t.translate("nav.allProjects"),
            icon: "pi pi-folder",
            routerLink: "/project-management/projects",
          },
          {
            label: this.t.translate("nav.goals"),
            icon: "pi pi-flag",
            routerLink: "/project-management/goals",
          },
        ],
      },
      {
        label: this.t.translate("nav.accounting"),
        icon: "pi pi-calculator",
        routerLink: "/accounting/overview",
        roles: [
          TeamMemberRoleEnum.SUPER_ADMIN,
          TeamMemberRoleEnum.ADMIN,
          TeamMemberRoleEnum.ACCOUNTANT,
        ],
        items: [
          {
            label: this.t.translate("nav.overview"),
            icon: "pi pi-chart-bar",
            routerLink: "/accounting/overview",
          },
          {
            label: this.t.translate("nav.allProjects"),
            icon: "pi pi-briefcase",
            routerLink: "/accounting/projects",
          },
          {
            label: this.t.translate("nav.totalCostInvoices"),
            icon: "pi pi-receipt",
            routerLink: "/accounting/invoices",
          },
          {
            label: this.t.translate("nav.payAppReceivables"),
            icon: "pi pi-dollar",
            routerLink: "/accounting/pay-apps",
          },
          {
            label: this.t.translate("nav.overhead"),
            icon: "pi pi-percentage",
            routerLink: "/accounting/overhead",
          },
          {
            label: this.t.translate("nav.vendors"),
            icon: "pi pi-building",
            routerLink: "/accounting/vendors",
          },
        ],
      },
      {
        label: this.t.translate("nav.admin"),
        icon: "pi pi-users",
        routerLink: "/admin/team",
        roles: [TeamMemberRoleEnum.SUPER_ADMIN, TeamMemberRoleEnum.ADMIN],
        items: [
          {
            label: this.t.translate("nav.teamManagement"),
            icon: "pi pi-users",
            routerLink: "/admin/team",
          },
          {
            label: this.t.translate("nav.crewManagement"),
            icon: "pi pi-user-edit",
            routerLink: "/admin/crew",
          },
          // {
          //   label: this.t.translate("nav.equipment"),
          //   routerLink: "/admin/equipment",
          // },
          {
            label: this.t.translate("nav.inventory"),
            icon: "pi pi-wrench",
            routerLink: "/admin/inventory",
          },
          {
            label: this.t.translate("nav.vendorCategories"),
            icon: "pi pi-tags",
            routerLink: "/admin/vendor-categories",
          },
        ],
      },
    ]);
  }

  private openAddNewProjectDialog(): void {
    const ref = this.dialogService.open(AddNewProjectDialogComponent, {
      showHeader: false,
      appendTo: "body",
      modal: true,
      inputValues: {},
    });
    ref.onClose
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((project) => !!project),
        switchMap((project) => this.projectService.addNewProject(project)),
      )
      .subscribe({
        next: (project: Project) => {
          this.projectService.triggerReload();
          this.router.navigate(["/project-management/projects", project.id]);
        },
        error: (error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: this.t.translate(
              "addNewProjectDialog.failedToCreateProject",
            ),
          });
        },
      });
  }

  private filterByRoles(items: NavItemInterface[]): NavItemInterface[] {
    const userRoles =
      this.userService.currentUser()?.roles?.map((role) => role.name) ?? [];

    return items.filter((item) => {
      if (!item.roles || item.roles.length === 0) {
        return true;
      }
      return item.roles.some((role) => userRoles.includes(role));
    });
  }
}
